import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import _ from 'lodash'

import { certificatesReselector as stateSelector } from 'reduxModules/certificates/selectors'
import { certificatesDispatchers as dispatchSelector } from 'reduxModules/certificates/dispatchers'
import { types, generalStatus } from 'reduxModules/certificates'

import Certificate from './Certificate'
import ClaimModal from './ClaimModal'
import GeneralCertificatePanel from './GeneralCertificatePanel'
import './styles.css'

const INITIAL_GENERAL = { status: generalStatus.initial, data: null }

// The backend answers 400 { name: [...] } when it has no name on file to
// print (SPEC-040 F8) — the cue to ask for one instead of failing.
const needsName = (res) =>
  res.payload?.status === 400 && Boolean(res.payload?.value?.name)

export function Certificates({
  certificates = null,
  defaultName = '',
  isMobile = false,
  token = '',
  general = INITIAL_GENERAL,
  hasCompletions = false,
  ...props
}) {
  const [claiming, setClaiming] = useState(null)
  const [naming, setNaming] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    props.loadCertificates()
    props.loadGeneralCertificate()
  }, [])

  // The course-completion email links to /certificates?course=<slug>; once the
  // list arrives, open the claim modal for that course if it's unclaimed.
  useEffect(() => {
    const slug = searchParams.get('course')
    if (!slug || !certificates) return
    const certificate = _.find(certificates, (c) => c.tutorial.slug === slug)
    if (certificate && !certificate.isClaimed) setClaiming(certificate)
    const cleaned = new URLSearchParams(searchParams)
    cleaned.delete('course')
    setSearchParams(cleaned, { replace: true })
  }, [certificates])

  const onSubmitClaim = ({ name }) =>
    props.claimCertificate(claiming.tutorial.slug, name).then((res) => {
      if (res.type === types.CERTIFICATE_CLAIM_SUCCESS) setClaiming(null)
    })

  // One click generates with the name on file; only a missing name (or the
  // user's own choice) opens the name dialog. `options.silent` is the
  // download-time refresh, which needs no toast.
  const onGenerate = (name, options) => {
    setGenerating(true)
    return props
      .generateGeneralCertificate(name, options)
      .then((res) => {
        if (res.type === types.CERTIFICATE_GENERAL_GENERATE_SUCCESS)
          setNaming(false)
        else if (needsName(res)) setNaming(true)
        return res
      })
      .finally(() => setGenerating(false))
  }

  if (!certificates) return null
  return (
    <div id="Certificates" className="account-section">
      <h3 className="account-section__title">My Certificates</h3>
      {hasCompletions && (
        <>
          <h4 className="certificates-subsection">All-Courses Certificate</h4>
          <GeneralCertificatePanel
            general={general}
            completionsCount={certificates.length}
            token={token}
            generating={generating}
            onGenerate={(options) => onGenerate(undefined, options)}
            onEditName={() => setNaming(true)}
            onRetry={props.loadGeneralCertificate}
          />
        </>
      )}
      {!_.isEmpty(certificates) && (
        <h4 className="certificates-subsection">Course Certificates</h4>
      )}
      {!_.isEmpty(certificates) && (
        <div className="certificates-list">
          {certificates.map((certificate) => (
            <Certificate
              key={certificate.tutorial.slug}
              certificate={certificate}
              token={token}
              onClaim={() => setClaiming(certificate)}
            />
          ))}
        </div>
      )}
      {_.isEmpty(certificates) && (
        <div className="no-results">
          <svg
            className="no-results__icon"
            viewBox="0 0 24 24"
            width="56"
            height="56"
            aria-hidden="true"
          >
            <path
              fill="currentColor"
              d="M12 2 3 6v6c0 5 3.8 8.6 9 10 5.2-1.4 9-5 9-10V6l-9-4Zm-1.3 13.3L7.4 12l1.4-1.4 1.9 1.9 4.1-4.1 1.4 1.4-5.5 5.5Z"
            />
          </svg>
          <span>
            You haven&apos;t completed any course yet. Finish watching a course
            and your Certificate of Completion will show up here.
          </span>
        </div>
      )}
      {claiming && (
        <ClaimModal
          certificate={claiming}
          defaultName={claiming.printedName || defaultName}
          isMobile={isMobile}
          onClose={() => setClaiming(null)}
          onSubmit={onSubmitClaim}
        />
      )}
      {naming && (
        <ClaimModal
          variant="general"
          regenerate={general.status === generalStatus.success}
          defaultName={general.data?.printedName || defaultName}
          isMobile={isMobile}
          onClose={() => setNaming(false)}
          onSubmit={({ name }) => onGenerate(name)}
        />
      )}
    </div>
  )
}

Certificates.propTypes = {
  loadCertificates: PropTypes.func.isRequired,
  claimCertificate: PropTypes.func.isRequired,
  loadGeneralCertificate: PropTypes.func.isRequired,
  generateGeneralCertificate: PropTypes.func.isRequired,
  certificates: PropTypes.arrayOf(PropTypes.object),
  defaultName: PropTypes.string,
  isMobile: PropTypes.bool,
  token: PropTypes.string,
  general: PropTypes.shape({
    status: PropTypes.oneOf(Object.values(generalStatus)).isRequired,
    data: PropTypes.object
  }),
  hasCompletions: PropTypes.bool
}

export default connect(stateSelector, dispatchSelector)(Certificates)
